// TASK-042 — alerts-panel widget. Composes the alert-rule entity (list
// fetch + row UI) with the toggle / threshold-edit / test-fire features
// and the inline create + delete affordances.

import type { AlertRuleResponse } from "@community-analytics/shared";

import { AlertRuleRow, useAlertRulesQuery } from "@entities/alert-rule";
import { AlertThresholdInput } from "@features/edit-alert-threshold";
import { AlertTestFireButton } from "@features/test-fire-alert";
import { AlertRuleToggle } from "@features/toggle-alert-rule";
import { RetryNotice } from "@shared/ui";

import { AlertRuleCreateForm } from "./AlertRuleCreateForm";
import { AlertRuleDeleteButton } from "./AlertRuleDeleteButton";

export interface AlertsPanelProps {
  communityId: string | null;
}

export function AlertsPanel({ communityId }: AlertsPanelProps) {
  const query = useAlertRulesQuery(communityId);

  if (communityId === null) {
    return (
      <p className="text-sm text-muted-foreground">
        Select a community to manage its alerts.
      </p>
    );
  }

  if (query.isPending) {
    return <AlertsPanelSkeleton />;
  }

  if (query.isError) {
    return (
      <RetryNotice
        title="Couldn't load alert rules"
        body={query.error?.message ?? "Check your connection and try again."}
        onRetry={() => void query.refetch()}
      />
    );
  }

  const rules = query.data ?? [];
  const existingTypes = rules.map((r) => r.type);

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h2 className="text-base font-semibold">Alert rules</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Get a private message when something notable happens in your community.
        </p>
      </header>

      <AlertRuleCreateForm
        communityId={communityId}
        existingTypes={existingTypes}
      />

      {rules.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          You have no alert rules yet — add one above.
        </p>
      ) : (
        <ul className="flex flex-col gap-3" data-testid="alerts-list">
          {rules.map((rule: AlertRuleResponse) => (
            <AlertRuleRow
              key={rule.id}
              rule={rule}
              toggleSlot={
                <AlertRuleToggle communityId={communityId} rule={rule} />
              }
              thresholdSlot={
                <AlertThresholdInput communityId={communityId} rule={rule} />
              }
              testFireSlot={
                <AlertTestFireButton communityId={communityId} rule={rule} />
              }
              deleteSlot={
                <AlertRuleDeleteButton
                  communityId={communityId}
                  ruleId={rule.id}
                />
              }
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function AlertsPanelSkeleton() {
  return (
    <div
      className="flex flex-col gap-3"
      role="status"
      aria-live="polite"
      aria-label="Loading alert rules"
    >
      <div className="h-20 animate-pulse rounded-lg bg-muted/50" />
      <div className="h-24 animate-pulse rounded-lg bg-muted/50" />
      <div className="h-24 animate-pulse rounded-lg bg-muted/50" />
    </div>
  );
}
